import {
  SubmissionSlotData,
  SubmissionSlotDetails,
  SubmissionSlots,
} from "types/SubmissionSlots";

import api from "API";
import * as Params from "API/params";

import { transformFileDownloadResponse } from "../Tasks/transformers";
import {
  DataSubmissionSlotParams,
  GetSubmissionSlotParams,
  GetSubmissionSlots,
  GetSubmissionSlotsByOrg,
  GetSubmissionSlotsGroupedByOrg,
  GetSubmissionTemplate,
  RequestToReopenParams,
  UpdateSubmissionSlotDataParams,
  submissionSlotBaseParams,
} from "./payloads";

const SubmissionGroupsEndpoint = "/organisations/:org/submission-groups/";

const extendedEndpoints = api.injectEndpoints({
  endpoints: (builder) => ({
    submissionSlots: builder.query<
      GetSubmissionSlots,
      Params.GenericParamMap<GetSubmissionSlotParams>
    >({
      query: (query) => {
        return {
          url:
            SubmissionGroupsEndpoint +
            `${query.submission_group_id}` +
            "/submission-slots",
          method: "GET",
          params: {
            page: query.page,
            ordering: Params.toString(query.ordering),
            time_period: Params.toArray(query.timePeriod),
            status: Params.toArray(query.submission_slots_status),
          },
        };
      },
    }),
    submissionSlotDetails: builder.query<
      SubmissionSlotDetails,
      Params.GenericParamMap
    >({
      query: (query) => ({
        url:
          SubmissionGroupsEndpoint +
          `${query.submissionGroupId}` +
          "/submission-slots" +
          `/${query.slotId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 0, //disable caching for this query
    }),
    actionRequiredSlot: builder.query<
      SubmissionSlots[],
      Params.GenericParamMap<GetSubmissionSlotParams>
    >({
      query: (query) => {
        return {
          url:
            SubmissionGroupsEndpoint +
            `${query.submission_group_id}` +
            "/submission-slots/action-required",
          method: "GET",
        };
      },
    }),
    updateSubmissionSlotData: builder.mutation<
      SubmissionSlotData,
      UpdateSubmissionSlotDataParams
    >({
      query: ({ groupId, slotId, ...params }) => ({
        url:
          SubmissionGroupsEndpoint +
          `${groupId}` +
          "/submission-slots" +
          `/${slotId}` +
          "/submit",
        method: "POST",
        body: params,
      }),
    }),
    updateSubmissionSlotIntegration: builder.mutation<
      void,
      submissionSlotBaseParams
    >({
      query: ({ groupId, slotId }) => ({
        url:
          SubmissionGroupsEndpoint +
          `${groupId}` +
          "/submission-slots" +
          `/${slotId}` +
          "/submit-from-integration",
        method: "POST",
      }),
    }),
    requestToReopen: builder.mutation<
      SubmissionSlotData,
      RequestToReopenParams
    >({
      query: ({ groupId, slotId }) => ({
        url:
          SubmissionGroupsEndpoint +
          `${groupId}` +
          "/submission-slots" +
          `/${slotId}` +
          "/request-reopen",
        method: "POST",
      }),
    }),
    getSubmissionTemplates: builder.query<
      { filename: string; file: Blob },
      GetSubmissionTemplate
    >({
      query: ({ groupId, slotId, activityId }) => ({
        url:
          SubmissionGroupsEndpoint +
          `${groupId}` +
          `/submission-slots/${slotId}` +
          (activityId
            ? `/activity/${activityId}/submission-template`
            : `/submission-templates`),

        method: "GET",
        responseHandler: async (response: Response) => {
          return transformFileDownloadResponse(response);
        },
      }),
    }),
    dataSubmissionSlots: builder.query<
      GetSubmissionSlotsByOrg,
      Params.GenericParamMap<DataSubmissionSlotParams>
    >({
      query: (query) => {
        const params: DataSubmissionSlotParams = {
          page: query.page,
          ordering: Params.toString(query.ordering),
          action_status: Params.toArray(query.tabStatus),
          time_period: Params.toArray(query.submissionPeriod),
          reporting_year: Params.toArray(query.reporting_years),
          entity: Params.toArray(query.entity),
          activity: Params.toArray(query.activity),
          status: query.status?.includes("data_issue")
            ? [...query.status, "validation_failed"]
            : (Params.toArray(
                query.status
              ) as DataSubmissionSlotParams["status"]),
          submission_group: Params.toArray(query.submissionGroup),
        };
        return {
          url: "/organisations/:org/submission-slots/",
          method: "GET",
          params: Params.enrichParamsWithCustomAttributes({ params, query }),
        };
      },
    }),
    submissionSlotCounts: builder.query<
      {
        all: number;
        dataIssue: number;
        draft: number;
        waitingForData: number;
      },
      Params.GenericParamMap<DataSubmissionSlotParams>
    >({
      query: (query) => {
        const params: DataSubmissionSlotParams = {
          time_period: Params.toArray(query.submissionPeriod),
          reporting_year: Params.toArray(query.reporting_years),
          entity: Params.toArray(query.entity),
          activity: Params.toArray(query.activity),
          status: query.status?.includes("data_issue")
            ? [...query.status, "validation_failed"]
            : (Params.toArray(
                query.status
              ) as DataSubmissionSlotParams["status"]),
          submission_group: Params.toArray(query.submissionGroup),
        };
        return {
          url: "/organisations/:org/submission-slots/counts/",
          method: "GET",
          params: Params.enrichParamsWithCustomAttributes({ params, query }),
        };
      },
    }),
    dataSubmissionSlotsGrouped: builder.query<
      GetSubmissionSlotsGroupedByOrg,
      Params.GenericParamMap<DataSubmissionSlotParams>
    >({
      query: (query) => {
        const params: DataSubmissionSlotParams = {
          page: query.page,
          ordering: Params.toString(query.ordering),
          action_status: Params.toArray(query.tabStatus),
          time_period: Params.toArray(query.submissionPeriod),
          reporting_year: Params.toArray(query.reporting_years),
          entity: Params.toArray(query.entity),
          activity: Params.toArray(query.activity),
          status: query.status?.includes("data_issue")
            ? [...query.status, "validation_failed"]
            : (Params.toArray(
                query.status
              ) as DataSubmissionSlotParams["status"]),
          submission_group: Params.toArray(query.submissionGroup),
        };
        return {
          url: "/organisations/:org/submission-slots/grouped/",
          method: "GET",
          params: Params.enrichParamsWithCustomAttributes({ params, query }),
        };
      },
    }),
  }),
});

export const {
  useSubmissionSlotsQuery,
  useSubmissionSlotDetailsQuery,
  useLazySubmissionSlotDetailsQuery,
  useActionRequiredSlotQuery,
  useUpdateSubmissionSlotDataMutation,
  useUpdateSubmissionSlotIntegrationMutation,
  useRequestToReopenMutation,
  useLazyGetSubmissionTemplatesQuery,
  useDataSubmissionSlotsQuery,
  useSubmissionSlotCountsQuery,
  useDataSubmissionSlotsGroupedQuery,
} = extendedEndpoints;
